import { fetchEntity, fetchEntityList, getJson, writeEntity } from '@/lib/entity-api';

export type BidPackageStatus = 'draft' | 'open' | 'closed' | 'cancelled';
export type InvitationStatus = 'invited' | 'responded';

export interface BidPackageRecord {
  id: string;
  package_id?: string | null;
  project_id: string;
  project?: { id: string; name: string } | null;
  title: string;
  cost_code_ids?: string[];
  description?: string | null;
  due_date: string;
  status: BidPackageStatus;
  created_at?: string;
}

export interface PackageInvitation {
  id: string;
  vendor_id: string;
  vendor_name: string;
  status: InvitationStatus;
  bid_id: string | null;
  bid_amount: number | null;
}

/** A bid that belongs to the package (its Linked Documents): also the one of a vendor whose assignment was revoked. */
export interface PackageBid {
  id: string;
  vendor_id: string | null;
  vendor_name: string;
  amount: number | null;
  status: string;
}

export interface BidPackagePayload {
  project_id?: string;
  title: string;
  cost_code_ids: string[];
  description?: string | null;
  due_date: string;
  status: BidPackageStatus;
  vendor_ids: string[];
}

/** Which statuses a package can be moved to from `from` (the database repeats the rule). */
export function nextPackageStatuses(from: BidPackageStatus | null): BidPackageStatus[] {
  switch (from) {
    case null: return ['draft', 'open'];
    case 'draft': return ['draft', 'open', 'cancelled'];
    case 'open': return ['open', 'closed', 'cancelled'];
    case 'closed': return ['closed', 'open', 'cancelled'];
    default: return ['cancelled'];
  }
}

export const PACKAGE_STATUS_LABEL: Record<BidPackageStatus, string> = { draft: 'Draft', open: 'Open', closed: 'Closed', cancelled: 'Cancelled' };
export const INVITATION_STATUS_LABEL: Record<InvitationStatus, string> = { invited: 'Invited', responded: 'Responded' };

/** Bid Packages data layer: every read and write goes through the backend API (generic entity API + two package routes: assignments and bids). */
export const bidPackageApi = {
  get: (id: string) => fetchEntity<BidPackageRecord>('bid_package', id),
  /**
   * The packages a bid can be attached to when it is created: what the caller reads (a vendor's RLS returns only the packages it is assigned to, in projects it belongs to; staff
   * the ones of their projects) that can still receive a bid -- Open for a vendor, Open or Closed for the company (which can record a late bid).
   */
  available: async (isVendor: boolean): Promise<BidPackageRecord[]> => {
    const rows = await fetchEntityList<BidPackageRecord>('bid_package');
    const accepted: BidPackageStatus[] = isVendor ? ['open'] : ['open', 'closed'];
    return rows.filter((row) => accepted.includes(row.status));
  },
  create: (payload: BidPackagePayload) => writeEntity<BidPackageRecord>('api/entities/bid_package', 'POST', payload, 'Failed to create the bid package'),
  update: (id: string, payload: Partial<BidPackagePayload>) => writeEntity<BidPackageRecord>(`api/entities/bid_package/${id}`, 'PUT', payload, 'Failed to update the bid package'),
  remove: (id: string) => writeEntity<void>(`api/entities/bid_package/${id}`, 'DELETE', {}, 'Failed to delete the bid package'),
  invitations: (id: string) => getJson<PackageInvitation[]>(`api/bid-packages/${id}/invitations`, 'Failed to load the invited vendors'),
  bids: (id: string) => getJson<PackageBid[]>(`api/bid-packages/${id}/bids`, 'Failed to load the bids of the package'),
};
